import {
  ContractDetailsQuery,
  ContractShowQuery,
  CreateContractMutation,
  UpdateContractMutation,
  type UpdateContractInput,
} from "@/admin/entities/contracts";
import { OrganizationsAutocompleteQuery } from "@/admin/entities/organizations";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import type { Schema } from "@/shared/components/Form/types";
import { ArrayItemRow } from "@/shared/components";
import type {
  ContractDetailsQueryType,
  ContractShowQueryType,
} from "@/types/graphql/admin.generated";
import { ContractDetails, ContractItemsAmountGuard } from "../domains";
import {
  BILLING_PERIOD_LABEL,
  CONTRACT_STATUS_LABEL,
} from "../domains/billingLabels";

const CURRENCY_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
];

// Целочисленные ключи объект отдаёт по возрастанию: месяц, квартал, полгода, год.
const BILLING_PERIOD_OPTIONS = Object.entries(BILLING_PERIOD_LABEL).map(
  ([value, label]) => ({ label, value: Number(value) })
);

const PAYMENT_PERIOD_TYPE_OPTIONS = [
  { label: "Предоплата, инвойс в начале периода", value: "prepaid" },
  { label: "Постоплата, инвойс в конце периода", value: "postpaid" },
];

const ITEM_TYPE_OPTIONS = [
  { label: "Лицензия", value: "LICENSE" },
  { label: "Доля с продаж", value: "REVENUE_SHARE" },
  { label: "Разовая работа", value: "ONE_TIME" },
];

/**
 * Фильтры раздела «Контракты». Правило проекта: все поля фильтра живут в
 * панели, чипов в шапке нет, поэтому у всех полей priority: "secondary".
 */
export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  organizationId: {
    label: "Организация",
    type: "autocomplete",
    query: OrganizationsAutocompleteQuery,
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  status: {
    label: "Статус",
    type: "select",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
    options: [
      { label: "Все", value: "" },
      { label: CONTRACT_STATUS_LABEL.pending, value: "PENDING" },
      { label: CONTRACT_STATUS_LABEL.active, value: "ACTIVE" },
      { label: CONTRACT_STATUS_LABEL.finished, value: "FINISHED" },
      { label: CONTRACT_STATUS_LABEL.canceled, value: "CANCELED" },
    ],
  },
  currency: {
    label: "Валюта",
    type: "select",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
    options: [{ label: "Все", value: "" }, ...CURRENCY_OPTIONS],
  },
  dateFrom: {
    label: "Срок действия от",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  dateTo: {
    label: "Срок действия до",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
};

/**
 * Пустые значения в фильтр не уходят: пустая строка статуса и валюты
 * означает «все», а неустановленная дата не должна уйти в переменные
 * запроса равенством по пустому значению.
 */
export function normalizeFilter(
  filter: Record<string, unknown>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === "" || value === null || value === undefined) continue;
    normalized[key] = value;
  }
  return normalized;
}

/**
 * Поля контракта. Строки услуг это массив: у строки доли с продаж суммы
 * нет, процент живёт в настройках организации, поэтому поля суммы у неё
 * скрыты, а подсказка отправляет в настройки.
 */
const contractSchema: Schema = {
  title: {
    label: "Название контракта",
    type: "text",
    required: true,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  currency: {
    label: "Валюта контракта",
    helperText: "Валюта ставки и строк контракта",
    type: "select",
    required: true,
    xs: 12,
    md: 3,
    fullWidth: true,
    options: CURRENCY_OPTIONS,
  },
  invoiceCurrency: {
    label: "Валюта счёта",
    // Пустое поле значит «как у контракта» (null на бэкенде), как и валюта,
    // совпавшая с валютой контракта. Поле очищается крестиком автокомплита.
    helperText:
      "Пусто: как у контракта. Пересчёт по курсу ЕЦБ на дату выставления счёта",
    type: "select",
    xs: 12,
    md: 3,
    fullWidth: true,
    options: CURRENCY_OPTIONS,
  },
  dateFrom: {
    label: "Начало",
    type: "date",
    required: true,
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  dateTo: {
    label: "Окончание",
    helperText:
      "Обязательно, если в контракте есть лицензия или разовая работа. Пусто это бессрочная доля с продаж",
    type: "date",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  billingPeriodMonths: {
    label: "Биллинг-период",
    type: "select",
    required: true,
    xs: 12,
    md: 4,
    fullWidth: true,
    options: BILLING_PERIOD_OPTIONS,
  },
  paymentPeriodType: {
    label: "Когда выставляем инвойс",
    type: "select",
    required: true,
    xs: 12,
    md: 5,
    fullWidth: true,
    options: PAYMENT_PERIOD_TYPE_OPTIONS,
  },
  paidPeriodDays: {
    label: "Срок оплаты, дней",
    type: "number",
    required: true,
    xs: 12,
    md: 3,
    fullWidth: true,
  },
  items: {
    label: "Строки услуг",
    type: "array",
    addText: "Добавить строку",
    itemComponent: ArrayItemRow,
    xs: 12,
    subschema: {
      type: {
        label: "Услуга",
        type: "select",
        required: true,
        xs: 12,
        md: 4,
        fullWidth: true,
        options: ITEM_TYPE_OPTIONS,
      },
      title: {
        label: "Название строки",
        type: "text",
        required: true,
        xs: 12,
        md: 4,
        fullWidth: true,
      },
      "amount.value": {
        label: "Сумма",
        helperText: "Лицензия: за биллинг-период. Разовая работа: итого",
        type: "number",
        xs: 12,
        md: 4,
        fullWidth: true,
        isVisible: ({ values }) => values?.type !== "REVENUE_SHARE",
      },
      revenueShareHint: {
        label: "Процент доли",
        type: "readonly",
        value:
          "Процент берётся из настроек организации, вкладка «Редактировать», поле «Тип комиссии платформы»",
        xs: 12,
        isVisible: ({ values }) => values?.type === "REVENUE_SHARE",
      },
    },
  },
  itemsAmountGuard: {
    type: "fieldComponent",
    component: ContractItemsAmountGuard,
    xs: 12,
  },
  note: {
    label: "Заметка",
    type: "markdown",
    xs: 12,
  },
};

/**
 * Организацию контракта выбирает пользователь только при создании: у
 * существующего контракта она уже привязана и в форме редактирования не
 * показывается (contractSchema).
 */
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Создание контракта",
    schema: {
      organizationId: {
        label: "Организация",
        type: "autocomplete",
        query: OrganizationsAutocompleteQuery,
        required: true,
        xs: 12,
        fullWidth: true,
      },
      ...contractSchema,
    },
    createMutation: CreateContractMutation,
  },
};

/** Дефолты формы создания повторяют дефолты бэкенда. */
export const createContractInitialVariables = {
  currency: "USD",
  invoiceCurrency: null,
  billingPeriodMonths: 1,
  paymentPeriodType: "prepaid",
  paidPeriodDays: 14,
  items: [{ type: "LICENSE", title: "Лицензия платформы" }],
};

/**
 * Диалог контракта: «Детали» первым шагом, «Редактировать» формой (спека
 * 2026-10-16 §5). Ключ формы остаётся main: на него ведут уже разосланные
 * ссылки и переход после создания контракта (CRUD.onSubmitCreateDialog).
 */
export const editSchema: EditSchemaDefinition = {
  details: {
    label: "Детали",
    type: "show" as const,
    showQuery: ContractDetailsQuery,
    showComponent: ContractDetails,
    getTitle: (data: unknown) =>
      (data as ContractDetailsQueryType | undefined)?.contract?.title ?? null,
  },
  main: {
    label: "Редактировать",
    type: "form" as const,
    schema: {
      ...contractSchema,
    },
    editQuery: ContractShowQuery,
    updateMutation: UpdateContractMutation,
    getEditData: (data: unknown): UpdateContractInput => {
      const { contract } = data as ContractShowQueryType;
      return {
        title: contract.title,
        currency: contract.currency,
        invoiceCurrency: contract.invoiceCurrency ?? null,
        dateFrom: contract.dateFrom,
        dateTo: contract.dateTo ?? null,
        billingPeriodMonths: contract.billingPeriodMonths,
        paymentPeriodType: contract.paymentPeriodType,
        paidPeriodDays: contract.paidPeriodDays,
        note: contract.note ?? "",
        items: contract.items.map((item) => ({
          type: item.type,
          title: item.title,
          amount: item.amount
            ? { value: item.amount.value, currency: contract.currency }
            : null,
        })),
      };
    },
  },
};
